import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { Radio } from "@/components/Radio/Radio";
import { classNames } from "@/utils/classNames";

import { MigrationLevel } from "../schema/pendingSchedules";

interface MigrationLevelSelectorProps {
	value: MigrationLevel;
	onChange: (level: MigrationLevel) => void;
}

const levels: MigrationLevel[] = ["safe", "silent", "forced"];

const levelStyles: Record<
	MigrationLevel,
	{ border: string; bg: string; badgeBg: string; badgeText: string }
> = {
	safe: {
		border: "border-brand-light",
		bg: "bg-blue-action-200",
		badgeBg: "bg-status-success-light",
		badgeText: "text-status-success",
	},
	silent: {
		border: "border-brand-light",
		bg: "bg-blue-action-200",
		badgeBg: "bg-blue-action-200",
		badgeText: "text-blue-action",
	},
	forced: {
		border: "border-status-error",
		bg: "bg-status-error-light",
		badgeBg: "bg-status-error-light",
		badgeText: "text-status-error",
	},
};

export const MigrationLevelSelector: FunctionComponent<MigrationLevelSelectorProps> = ({
	value,
	onChange,
}) => {
	const { t } = useTranslation();

	return (
		<div className="flex flex-col gap-3">
			<div>
				<div className="text-text text-sm font-medium">{t("pendingSchedules.migrationLevel")}</div>
				<div className="text-text-meta text-xs mt-1">
					{t("pendingSchedules.migrationLevelDescription")}
				</div>
			</div>

			<div className="flex flex-col gap-2">
				{levels.map((level) => {
					const isSelected = value === level;
					const styles = levelStyles[level];

					return (
						<div
							key={level}
							className={classNames(
								"flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
								isSelected
									? `${styles.border} ${styles.bg}`
									: "border-border-light bg-white hover:bg-brand-white"
							)}
							data-testid={`migration-level-${level}`}
							onClick={() => onChange(level)}
						>
							<div className="mt-0.5">
								<Radio isChecked={isSelected} name="migrationLevel" value={level} />
							</div>
							<div className="flex-1 min-w-0">
								<div className="flex items-center gap-2">
									<span className="text-text text-sm font-medium">
										{t(`pendingSchedules.${level}`)}
									</span>
									<span
										className={classNames(
											"text-xs px-2 py-0.5 rounded-full font-medium",
											styles.badgeBg,
											styles.badgeText
										)}
									>
										{t(`pendingSchedules.${level}Badge`)}
									</span>
								</div>
								<div className="text-text-meta text-xs mt-1">
									{t(`pendingSchedules.${level}Description`)}
								</div>
							</div>
						</div>
					);
				})}
			</div>

			{value === "forced" && (
				<>
					<div
						className="flex flex-col gap-1 border border-border-light rounded-lg bg-brand-white p-4"
						data-testid="reset-rule"
					>
						<span>{t("pendingSchedules.resetRuleTitle")}</span>

						<div className={classNames("overflow-hidden transition-all duration-200 max-h-40")}>
							<div className="text-text-meta text-xs">
								{t("pendingSchedules.resetRuleDescription")}
							</div>
						</div>
					</div>
					<div
						className="flex items-start gap-2 p-3 rounded-lg bg-status-error-light border border-status-error"
						data-testid="forced-warning"
					>
						<div className="text-status-error text-xs">{t("pendingSchedules.forcedWarning")}</div>
					</div>
				</>
			)}
		</div>
	);
};
